import { useRef, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useMastermind } from "@/contexts/MastermindContext";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import {
  bumpThreadInSummariesCache,
  patchThreadSummariesCache,
  prependThreadToSummariesCache,
} from "@/lib/sidebarRecencyCache";
import { chatThreadsService } from "@/services/chatThreadsService";
import { supabase, isSupabaseConfigured } from "@/lib/supabase";
import { chatMessages$ } from "@/state/chatMessagesLegend";
import {
  clearPendingDraftsCoveredByDb,
  listUnverifiedPendingDrafts,
  type PendingThreadDraft,
} from "@/lib/pendingThreadStore";

const DEBUG_THREAD_ACCESS =
  import.meta.env.DEV || import.meta.env.VITE_DEBUG_THREAD_ACCESS === "true";

/** True when a thread still needs AI auto-titling. */
export function threadNeedsAutoTitle(title?: string | null): boolean {
  const t = title?.trim();
  return !t || t === "Untitled";
}

export interface UseChatThreadOptions {
  updateMessages: (updater: any) => void;
  /** When opening /chat?threadId=X, pass it here so the thread loads on first render before context updates */
  threadIdFromUrl?: string | null;
  /** When opening /chat?threadId=X&spaceId=Y from a shared space */
  spaceIdFromUrl?: string | null;
  /** Active space selection in ChatWorkspace (fallback for shared-space RPC context) */
  selectedSpaceId?: string | null;
  /** Current user — enables draft vs DB reconciliation on load */
  userId?: string | null;
  /** Called when a local draft has more messages than the DB row for this thread */
  onReconcileDraft?: (draft: PendingThreadDraft) => void;
  /** Called when DB ends with a user message and no following assistant */
  onIncompleteTurn?: (threadId: string) => void;
}

export function useChatThread(options: UseChatThreadOptions): {
  currentThread: ChatThreadData | null | undefined;
  currentThreadId: string | null;
  setCurrentThreadId: (id: string | null) => void;
  createThreadMutation: any;
  updateThreadMutation: any;
  generateMetadataMutation: any;
  shouldAutoTitle: (threadId: string) => boolean;
  lastLoadedThreadIdRef: React.MutableRefObject<string | null>;
} {
  const { updateMessages, threadIdFromUrl, spaceIdFromUrl, selectedSpaceId, userId, onReconcileDraft, onIncompleteTurn } = options;

  const { currentThreadId, setCurrentThreadId } = useMastermind();
  const { currentWorkspaceId } = useWorkspace();
  const lastLoadedThreadIdRef = useRef<string | null>(null);
  const lastLoadedThreadVersionRef = useRef<string | null>(null);
  const prevEffectiveThreadIdRef = useRef<string | null>(null);
  const lastTouchedThreadIdRef = useRef<string | null>(null);
  const realtimeRefreshInFlightRef = useRef(false);
  const realtimeRefreshQueuedRef = useRef(false);
  const realtimeRefreshTimerRef = useRef<number | null>(null);
  const loadErrorToastShownRef = useRef<string | null>(null);
  const incompleteTurnToastShownRef = useRef<string | null>(null);
  const { toast } = useToast();
  const workspaceKey = currentWorkspaceId ?? "personal";
  const spaceContextKey = spaceIdFromUrl ?? selectedSpaceId ?? "none";

  const effectiveThreadId = threadIdFromUrl ?? currentThreadId;

  const resolveSpaceContextId = (thread?: { linkedSpaceIds?: string[] | null } | null): string | null => {
    if (spaceIdFromUrl) return spaceIdFromUrl;
    if (selectedSpaceId) return selectedSpaceId;
    const linked = thread?.linkedSpaceIds;
    if (Array.isArray(linked) && linked.length > 0 && linked[0]) {
      return String(linked[0]);
    }
    return null;
  };

  const {
    data: currentThread,
    isError,
    error,
    isFetched,
    isSuccess,
  } = useQuery<ChatThreadData | null>({
    queryKey: ["/api/chat-threads", effectiveThreadId, workspaceKey, spaceContextKey],
    enabled: !!effectiveThreadId,
    queryFn: async () => {
      if (!effectiveThreadId) return null;
      if (DEBUG_THREAD_ACCESS) {
        console.log("[useChatThread] fetch", {
          effectiveThreadId,
          workspaceKey,
          spaceIdFromUrl,
          selectedSpaceId,
        });
      }
      return await chatThreadsService.getChatThread(
        effectiveThreadId,
        currentWorkspaceId ?? null,
        resolveSpaceContextId()
      );
    },
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const resolveSpaceContextForThreadId = (threadId: string): string | null => {
    const cached = queryClient.getQueryData<{ linkedSpaceIds?: string[] | null; title?: string | null } | null>([
      "/api/chat-threads",
      threadId,
      workspaceKey,
      spaceContextKey,
    ]);
    return resolveSpaceContextId(cached ?? currentThread ?? null);
  };

  const shouldAutoTitle = (threadId: string): boolean => {
    const cached = queryClient.getQueryData<{ title?: string | null } | null>([
      "/api/chat-threads",
      threadId,
      workspaceKey,
      spaceContextKey,
    ]);
    return threadNeedsAutoTitle(cached?.title);
  };

  useEffect(() => {
    if (!DEBUG_THREAD_ACCESS || !effectiveThreadId) return;
    console.log("[useChatThread] query state", {
      effectiveThreadId,
      isSuccess,
      isError,
      isFetched,
      hasData: currentThread != null,
      messageCount: Array.isArray((currentThread as any)?.messages)
        ? (currentThread as any).messages.length
        : 0,
      error: error instanceof Error ? error.message : error,
    });
  }, [effectiveThreadId, isSuccess, isError, isFetched, currentThread, error]);

  useEffect(() => {
    if (!effectiveThreadId) {
      loadErrorToastShownRef.current = null;
      return;
    }
    if (isError && error) {
      if (loadErrorToastShownRef.current !== effectiveThreadId) {
        loadErrorToastShownRef.current = effectiveThreadId;
        toast({
          title: "Could not load conversation",
          description: error instanceof Error ? error.message : "Please try again.",
          variant: "destructive",
        });
      }
      return;
    }
    if (isFetched && !currentThread && !isError) {
      if (loadErrorToastShownRef.current !== effectiveThreadId) {
        loadErrorToastShownRef.current = effectiveThreadId;
        toast({
          title: "Could not load conversation",
          description: "You may not have access to this thread in this space.",
          variant: "destructive",
        });
      }
    }
  }, [effectiveThreadId, isFetched, isError, error, currentThread, toast]);

  useEffect(() => {
    if (!effectiveThreadId || !isSupabaseConfigured) return;

    const threadQueryKey = ["/api/chat-threads", effectiveThreadId, workspaceKey, spaceContextKey] as const;
    const runRefresh = async () => {
      if (realtimeRefreshInFlightRef.current) {
        realtimeRefreshQueuedRef.current = true;
        return;
      }
      realtimeRefreshInFlightRef.current = true;
      try {
        const cached = queryClient.getQueryData<ChatThreadData | null>(threadQueryKey);
        const fresh = await chatThreadsService.getChatThread(
          effectiveThreadId,
          currentWorkspaceId ?? null,
          resolveSpaceContextId(cached ?? currentThread ?? null)
        );
        queryClient.setQueryData(threadQueryKey, fresh);
      } catch (err) {
        console.error("Realtime thread refresh failed:", err);
      } finally {
        realtimeRefreshInFlightRef.current = false;
        if (realtimeRefreshQueuedRef.current) {
          realtimeRefreshQueuedRef.current = false;
          void runRefresh();
        }
      }
    };
    const channel = supabase
      .channel(`chat-thread-live:${workspaceKey}:${spaceContextKey}:${effectiveThreadId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "chat-threads",
          filter: `id=eq.${effectiveThreadId}`,
        },
        async (payload) => {
          if (payload.eventType === "DELETE") {
            queryClient.setQueryData(threadQueryKey, null);
            return;
          }

          if (realtimeRefreshTimerRef.current != null) {
            window.clearTimeout(realtimeRefreshTimerRef.current);
          }
          realtimeRefreshTimerRef.current = window.setTimeout(() => {
            realtimeRefreshTimerRef.current = null;
            void runRefresh();
          }, 250);
        }
      )
      .subscribe();

    return () => {
      if (realtimeRefreshTimerRef.current != null) {
        window.clearTimeout(realtimeRefreshTimerRef.current);
        realtimeRefreshTimerRef.current = null;
      }
      realtimeRefreshQueuedRef.current = false;
      realtimeRefreshInFlightRef.current = false;
      supabase.removeChannel(channel).catch(() => {
        // noop
      });
    };
  }, [effectiveThreadId, currentWorkspaceId, spaceContextKey, currentThread]);

  useEffect(() => {
    if (!effectiveThreadId) return;

    const threadQueryKey = ["/api/chat-threads", effectiveThreadId, workspaceKey, spaceContextKey] as const;
    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      queryClient.invalidateQueries({ queryKey: threadQueryKey });
    };

    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, [effectiveThreadId, workspaceKey, spaceContextKey]);

  useEffect(() => {
    if (threadIdFromUrl && threadIdFromUrl !== currentThreadId) {
      setCurrentThreadId(threadIdFromUrl);
    }
  }, [threadIdFromUrl, currentThreadId, setCurrentThreadId]);

  // Clear stale Legend messages immediately when navigating to a different thread
  useEffect(() => {
    const prev = prevEffectiveThreadIdRef.current;
    prevEffectiveThreadIdRef.current = effectiveThreadId ?? null;

    if (!effectiveThreadId) {
      if (prev !== null) {
        lastLoadedThreadVersionRef.current = null;
        updateMessages([]);
      }
      return;
    }

    if (prev !== null && prev !== effectiveThreadId) {
      lastLoadedThreadIdRef.current = null;
      lastLoadedThreadVersionRef.current = null;
      updateMessages([]);
    }
  }, [effectiveThreadId, updateMessages]);

  useEffect(() => {
    if (!effectiveThreadId) {
      lastTouchedThreadIdRef.current = null;
      return;
    }
    if (!isSuccess || !currentThread) return;
    if (lastTouchedThreadIdRef.current === effectiveThreadId) return;
    lastTouchedThreadIdRef.current = effectiveThreadId;

    const now = new Date().toISOString();
    bumpThreadInSummariesCache(effectiveThreadId, now);

    void chatThreadsService
      .touchChatThread(effectiveThreadId, currentWorkspaceId ?? null)
      .catch((err) => {
        console.warn("Failed to touch chat thread:", err);
      });
  }, [effectiveThreadId, isSuccess, currentThread, currentWorkspaceId]);

  const generateMetadataMutation = useMutation({
    mutationFn: async (threadId: string) => {
      const spaceContextId = resolveSpaceContextForThreadId(threadId);
      return await chatThreadsService.generateMetadata(threadId, {
        workspaceId: currentWorkspaceId ?? null,
        spaceContextId,
      });
    },
    onError: (error: unknown) => {
      console.error("[useChatThread] generateMetadata failed:", error);
      if (DEBUG_THREAD_ACCESS) {
        toast({
          title: "Auto-title failed",
          description: error instanceof Error ? error.message : "Could not generate conversation title.",
          variant: "destructive",
        });
      }
    },
    onSuccess: (updatedThread: { id?: string; title?: string }, threadId) => {
      if (updatedThread?.title) {
        toast({
          title: "Conversation titled",
          description: `"${updatedThread.title}"`,
        });
      }
      if (threadId) {
        queryClient.setQueryData(
          ["/api/chat-threads", threadId, workspaceKey, spaceContextKey],
          (old: any) => updatedThread ?? old
        );
        if (updatedThread?.title) {
          patchThreadSummariesCache((item) =>
            item?.id === threadId ? { ...item, title: updatedThread.title } : item
          );
        }
      }
    },
  });

  const createThreadMutation = useMutation({
    mutationFn: async (data: {
      userId: string;
      messages: unknown[];
      linkedSpaceIds: string[];
      contextMode: string;
      chatMode: string;
      agentId?: string | null;
      topicId?: string | null;
    }) => {
      return await chatThreadsService.createChatThread(
        {
          ...data,
          title: null,
          status: "active",
        },
        currentWorkspaceId ?? null
      );
    },
    onError: (error: unknown) => {
      const message =
        error instanceof Error ? error.message : "Failed to create chat thread.";
      toast({
        title: "Thread not saved to database",
        description: `${message} Your messages are kept in browser local storage until save is confirmed.`,
        variant: "destructive",
      });
    },
    onSuccess: (thread: { id: string; messages?: unknown[] }) => {
      setCurrentThreadId(thread.id);
      queryClient.setQueryData(
        ["/api/chat-threads", thread.id, workspaceKey, spaceContextKey],
        thread as any
      );
      prependThreadToSummariesCache({
        ...(thread as Record<string, unknown>),
        messageCount: Array.isArray(thread.messages) ? thread.messages.length : 0,
        updatedAt: (thread as { updatedAt?: string }).updatedAt ?? new Date().toISOString(),
      });
      lastLoadedThreadIdRef.current = thread.id;
      const messages = Array.isArray(thread.messages) ? thread.messages : [];
      if (messages.length >= 2 && threadNeedsAutoTitle(thread.title)) {
        generateMetadataMutation.mutate(thread.id);
      }
    },
  });

  const updateThreadMutation = useMutation({
    onMutate: async ({
      id,
      data,
    }: {
      id: string;
      data: Partial<{ messages: unknown[] }>;
    }) => {
      if (!data.messages) return;
      const key = ["/api/chat-threads", id, workspaceKey, spaceContextKey] as const;
      queryClient.setQueryData(key, (old: ChatThreadData | null | undefined) => {
        if (!old) return old;
        return {
          ...old,
          messages: data.messages,
          updatedAt: new Date().toISOString(),
        };
      });
    },
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: Partial<{
        messages: unknown[];
        contextMode: string;
        chatMode: string;
        agentId?: string | null;
        title?: string;
        tags?: string[];
        linkedSpaceIds?: string[];
        topicId?: string | null;
      }>;
    }) => {
      const cached = queryClient.getQueryData<ChatThreadData | null>([
        "/api/chat-threads",
        id,
        workspaceKey,
        spaceContextKey,
      ]);
      const spaceContextId = resolveSpaceContextId(cached ?? currentThread ?? null);
      if (DEBUG_THREAD_ACCESS) {
        console.log("[useChatThread] update", { id, spaceContextId, keys: Object.keys(data) });
      }
      const result = await chatThreadsService.updateChatThread(
        id,
        data,
        currentWorkspaceId ?? null,
        spaceContextId
      );
      return { result, threadId: id };
    },
    onSuccess: ({ result, threadId }: { result: any; threadId: string }) => {
      queryClient.setQueryData(
        ["/api/chat-threads", threadId, workspaceKey, spaceContextKey],
        (old: any) => result ?? old
      );
      if (result) {
        patchThreadSummariesCache((item) =>
          item?.id === threadId
            ? {
                ...item,
                title: result.title ?? item.title,
                topic: result.topic ?? item.topic,
                topicId: result.topicId ?? item.topicId,
                topicIds: result.topicIds ?? item.topicIds,
                tags: result.tags ?? item.tags,
                linkedSpaceIds: result.linkedSpaceIds ?? item.linkedSpaceIds,
                agentId: result.agentId ?? item.agentId,
                contextMode: result.contextMode ?? item.contextMode,
                status: result.status ?? item.status,
                isLocked: result.isLocked ?? item.isLocked,
                lockedAt: result.lockedAt ?? item.lockedAt,
                isArchived: result.isArchived ?? item.isArchived,
                archivedAt: result.archivedAt ?? item.archivedAt,
                archiveSummaryDocId: result.archiveSummaryDocId ?? item.archiveSummaryDocId,
                includeInRag: result.includeInRag ?? item.includeInRag,
                updatedAt: result.updatedAt ?? item.updatedAt,
              }
            : item
        );
      }
    },
  });

  useEffect(() => {
    if (!effectiveThreadId && lastLoadedThreadIdRef.current !== null) {
      lastLoadedThreadIdRef.current = null;
      lastLoadedThreadVersionRef.current = null;
      updateMessages([]);
      return;
    }

    const thread = currentThread as {
      id?: string | number;
      messages?: unknown[];
      updatedAt?: string | number | null;
      createdAt?: string | number | null;
    } | null;
    const loadedForCurrent = thread && String(thread.id) === String(effectiveThreadId);
    const threadChanged = lastLoadedThreadIdRef.current !== effectiveThreadId;
    const threadVersion = loadedForCurrent
      ? `${thread.updatedAt ?? thread.createdAt ?? ""}::${Array.isArray(thread.messages) ? thread.messages.length : -1}`
      : null;
    const versionChanged =
      threadVersion != null && threadVersion !== lastLoadedThreadVersionRef.current;

    if (DEBUG_THREAD_ACCESS) {
      console.log("[useChatThread] restore messages", {
        effectiveThreadId,
        loadedForCurrent,
        threadChanged,
        versionChanged,
        messageCount: loadedForCurrent && Array.isArray(thread.messages) ? thread.messages.length : 0,
      });
    }

    if (loadedForCurrent && (threadChanged || versionChanged) && Array.isArray(thread.messages)) {
      const currentMessages = chatMessages$.peek();
      const restoredMessages = thread.messages.map((msg: unknown) => {
        const m = msg as Record<string, unknown>;
        return {
          id: m.id || `fallback-${Date.now()}`,
          role: m.role,
          content: m.content,
          timestamp: m.timestamp ? new Date(m.timestamp as string) : new Date(),
          citations: m.citations ?? undefined,
          contextCitations: m.contextCitations ?? undefined,
          images: m.images ?? undefined,
          model: m.model ?? undefined,
          autoSelected: m.autoSelected ?? false,
          status: m.status ?? undefined,
          errorMessage: m.errorMessage ?? undefined,
          errorKind: m.errorKind ?? undefined,
          receiptLine: typeof m.receiptLine === "string" ? m.receiptLine : undefined,
        };
      });
      // Only preserve in-flight streaming on the same thread — never block cross-thread navigation
      if (!threadChanged && currentMessages.length > restoredMessages.length) {
        return;
      }
      updateMessages(restoredMessages);
      lastLoadedThreadIdRef.current = effectiveThreadId;
      lastLoadedThreadVersionRef.current = threadVersion;

      if (userId && effectiveThreadId) {
        const drafts = listUnverifiedPendingDrafts(userId).filter(
          (d) => d.threadId && String(d.threadId) === String(effectiveThreadId)
        );
        const richestDraft = drafts.sort((a, b) => b.messages.length - a.messages.length)[0];
        if (richestDraft && richestDraft.messages.length > restoredMessages.length) {
          onReconcileDraft?.(richestDraft);
        } else {
          // DB already has these messages — drop stale local drafts so recover UI stays quiet.
          clearPendingDraftsCoveredByDb(userId, String(effectiveThreadId), restoredMessages.length);
          if (restoredMessages.length > 0) {
            const last = restoredMessages[restoredMessages.length - 1] as { role?: string };
            if (last.role === "user" && !richestDraft) {
              onIncompleteTurn?.(String(effectiveThreadId));
              if (incompleteTurnToastShownRef.current !== String(effectiveThreadId)) {
                incompleteTurnToastShownRef.current = String(effectiveThreadId);
                toast({
                  title: "Response may not have saved",
                  description:
                    "The last message in this thread is your prompt with no assistant reply in the database. Use Retry on that message to regenerate.",
                  variant: "destructive",
                });
              }
            }
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveThreadId, currentThread]);

  return {
    currentThread,
    currentThreadId: effectiveThreadId ?? currentThreadId,
    setCurrentThreadId,
    createThreadMutation,
    updateThreadMutation,
    generateMetadataMutation,
    shouldAutoTitle,
    lastLoadedThreadIdRef,
  };
}
